import { IUpdateItem, IValidator, IBaseFields, ICreatorItem, IMessageModalProps, IDisplayItem, ICreateItem } from "../interfaces"
import { DateTime } from "luxon"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { v4 as uuidv4 } from "uuid"
import { IDocumentUpdateFormProps } from "../../../../@types/documents"
import { DocumentType, IdTableVariant, SearchOption } from "../../../../@types/enumsGlobal"
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel"
import { Accordion } from "../../../../components/controls/accordion"
import FlexRow from "../../../../components/controls/FlexRow"
import GridSelectorInput from "../../../../components/controls/GridSelectorInput"
import GridWrapper from "../../../../components/controls/GridWrapper"
import { DateInput, NumberInput, TextInput } from "../../../../components/controls/inputs"
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator"
import { ChangeableGrid, IChangeDataGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid"
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments"
import useOperativeReserve from "../../../../components/lotSelector/useOperativeReserve"
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow"
import { IOption, Select } from "../../../../components/selects/select"
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel"
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common"
import { IGoodsDTO } from "../../../../libs/coreapi-dto/dirs/goods"
import { ISubstanceViewDTO } from "../../../../libs/coreapi-dto/dirs/substance"
import { ITradeNameViewDTO } from "../../../../libs/coreapi-dto/dirs/tradeName"
import { IRequestForMovementCreateDTO, IRequestForMovementEditDTO, IRequestForMovementGetDTO } from "../../../../libs/coreapi-dto/documents/requestForMovement"
import { IRequestForMovementItemCreateDTO, IRequestForMovementItemGetDTO } from "../../../../libs/coreapi-dto/documents/requestForMovementItem"
import { GoodsDataProvider } from "../../../../Services/DataProviders/GoodsDataProvider"
import { RequestForMovementDataProvider } from "../../../../Services/DataProviders/RequestForMovementDataProvider"
import { addValueToArray } from "../../../../system/functions/addValToArray"
import { getStyleCell } from "../../../../system/functions/getStyleCell"
import { onCanChangeData } from "../../../../system/functions/onCanChangeData"
import { useForm } from "../../../../system/hooks/useForm"
import useGridFilter from "../../../../system/hooks/useGridFilter"
import { useAppContext } from "../../../../system/providers/appContextProvider"
import { useUserContext } from "../../../../system/providers/userContextProvider"
import { checkAccessStatus, checkLockStatus } from "../../../CommonHelperFunctions"
import { GoodsSelectorModal } from "../../../Dictionaries/Goods"
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair"
import styles from "../../../styles/index.module.scss"
import RequestForMovementItemsGridPluginSettings from "../RequestForMovementItems/RequestForMovementItemsGridPluginSettings"
import { RequestForMovementCreatorItemModal } from "./RequestForMovementItemCreator/RequestForMovementItemCreatorModal"
import { RequestForMovementUpdateItemModal } from "./RequestForMovementItemCreator/RequestForMovementItemUpdateModal"
import createRequestForMovementStyles from "./styles/CreateRequestForMovement.module.scss"
import { useSupplierSelection } from "../../../../system/hooks/useSupplierSelection"
import { Checkbox } from '../../../../components/checkboxes/checkbox';
import SubstanceByStoreSelectorModal from "../../../Dictionaries/Substance/SubstanceByStoreSelectorModal"
import TradeNameByStoreSelectorModal from "../../../Dictionaries/TradeName/TradeNameByStoreSelectorModal"
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider"
import { IContractorDTO } from "../../../../libs/coreapi-dto/dirs/contractor"

export const RequestForMovementUpdateForm: FC<IDocumentUpdateFormProps<IRequestForMovementGetDTO, IRequestForMovementItemGetDTO[], IRequestForMovementEditDTO>> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const { t } = useTranslation();

    const baseT = (value: string) => t("documents.requestForMovement." + value);
    const errorsT = (value: string) => t("errors." + value);
    const defaultSearchOption = {
        displayName: baseT("searchByMnn"),
        value: SearchOption.substanceName
    } as IOption;

    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const contractorDP = new ContractorDataProvider(appContext.coreApiService);
    const requestForMovementDP = new RequestForMovementDataProvider(appContext.coreApiService);
    const operativeReserve = useOperativeReserve();
    const lockingDocuments = useLockingDocuments();
    const [, setSupplierSelection] = useSupplierSelection();

    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const documentStateProc = props.variant === "edit" && (props.document?.documentState === "proc" || lockFromPermission) ? true : false;
    const onlyRead = (props.variant === "edit" || props.variant === "copy") && (props.document?.documentState === "proc" || props.document?.documentState === "sent" || props.document?.documentState === "secured" || lockFromPermission) ? true : false;
    const [gridFilter, dispatchGridFilter] = useGridFilter();

    const store: IEntitySimpleDTO | null = props.document?.store ?? null;
    const contractor: IEntitySimpleDTO | null = props.document?.contractor ?? null;

    const [data, setData] = useState<IRequestForMovementCreateDTO>(
        props.documentUpdate ??
        ({
            idGlobal: props.idGlobal,
            documentDate: DateTime.now(),
            items: [] as IRequestForMovementItemCreateDTO[],
        } as IRequestForMovementCreateDTO)
    );
    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);
    const [totalCount] = useState<number>(0);
    const [mnemocode, setMnemocode] = useState<string>(props.document?.mnemocode ?? "");
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({
        show: false,
        message: "",
    });
    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [creatorItem, setCreatorItem] = useState<ICreatorItem>({
        show: false,
        item: null,
    });
    const [updateItem, setUpdateItem] = useState<IUpdateItem>({
        show: false,
        item: null,
    });
    const [baseFields, setBaseFields] = useState<IBaseFields>({
        idContractorExternalGlobal: "" as string,
        idStoreExternalGlobal: "" as string,
        idSupplierExternalGlobal: "" as string,
        idSupplierStoreExternalGlobal: "" as string,

        dosage: "" as string,
        nFv: "" as string,
        quantity: 0 as number,
    });
    const [quantityGoods, setQuantityGoods] = useState<number>(0);
    const [quantityRows, setQuantityRows] = useState<number>(0);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([])
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([])
    const [isGetFromStore, setIsGetFromStore] = useState<boolean>(true);
    const [isQuantityControl, setIsQuantityControl] = useState<boolean>(JSON.parse(localStorage.getItem("quantityControl") as string) ?? false);
    const isQuantityControlDisabled = checkLockStatus("RequestForMovementQuantityControl", userContext.userPermission);
    const isQuantityControlVisible = checkAccessStatus("RequestForMovementQuantityControl", userContext.userPermission);
    
    const [contractorFrom, setContractorFrom] = useState<IContractorDTO>();
    const [contractorTo, setContractorTo] = useState<IContractorDTO>();

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            idContractorExternalGlobal: {
                required: {
                    value: data?.idContractorExternalGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            idStoreExternalGlobal: {
                required: {
                    value: data?.idStoreExternalGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            idSupplierExternalGlobal: {
                required: {
                    value: data?.idSupplierExternalGlobal ? false : true,
                    message: errorsT("required"),
                },
                custom: {
                    isValid: (value) => {
                        return value !== data.idContractorExternalGlobal
                    },
                    message: errorsT("sameContractorAndSupplier"),
                },
            },
            idSupplierStoreExternalGlobal: {
                required: {
                    value: data?.idSupplierStoreExternalGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            documentDate: {
                required: {
                    value: data?.documentDate ? false : true,
                    message: errorsT("required"),
                },
            },
            sameContractorAndSupplier: {
                custom: {
                    isValid: () => {
                        return data.idSupplierExternalGlobal !== data.idContractorExternalGlobal
                    },
                    message: errorsT("sameContractorAndSupplier"),
                },
            },
        },
    })

    useEffect(() => {
        setSearchOption(defaultSearchOption)
    }, []);

    useEffect(() => {
        if (props.documentItems) {
            addDisplayItems(props.documentItems)
        }
    }, []);

    useEffect(() => {
        return () => {
            localStorage.removeItem('isSupplierSelection')
        };
    }, []);

    useEffect(() => {
        const handleTabClose = (event) => {
            operativeReserve.deleteAll({
                idTable: IdTableVariant.RequestForMovement,
                idDocument: data.idGlobal as string,
            });
            lockingDocuments.delete({
                idTable: IdTableVariant.RequestForMovement,
                idDocument: data.idGlobal as string,
            });
            return (event.returnValue = "")
        }
        window.addEventListener("beforeunload", handleTabClose)
        return () => {
            window.removeEventListener("beforeunload", handleTabClose)
        }
    }, []);
    
    useEffect(() => {
        if ((props.variant === "edit" || props.variant === "copy") && props.documentUpdate && props.documentItems && props.document) {
            if (props.variant === "edit" || props.variant === "copy")
                lockingDocuments.check(data.idGlobal as string, (e) => {
                    if (e) props.cancel()
                    else
                        lockingDocuments.send(
                            {
                                idTable: IdTableVariant.RequestForMovement,
                                idDocument: data.idGlobal as string,
                            },
                            (e) => {}
                        )
                })

            const initialItems = props.documentItems.map((item) => ({
                idGlobal: item.idGlobal,
                idSubstanceExternalGlobal: item?.substance?.idGlobal || "",
                idTradeNameExternalGlobal: item?.tradeName?.idGlobal || "",
                idCureFormExternalGlobal: item?.cureForm?.idGlobal || "",

                quantity: item.quantity || 0,
                dosage: item.dosage || "",
                nFv: item.nFv || "",
                cureForm: item.cureForm || { displayName: "", idGlobal: "" },
                substance: item.substance || { displayName: "", idGlobal: "" },
                tradeName: item.tradeName || { displayName: "", idGlobal: "" },
                retailCostInfo: item.retailCostInfo || {
                    vat: 0,
                    price: 0,
                    vatPrice: 0,
                    priceIncVat: 0,
                    sum: 0,
                    vatSum: 0,
                    sumIncVat: 0,
                    adprice: 0,
                },
                supplierCostInfo: item.supplierCostInfo || {
                    vat: 0,
                    price: 0,
                    vatPrice: 0,
                    priceIncVat: 0,
                    sum: 0,
                    vatSum: 0,
                    sumIncVat: 0,
                    adprice: 0,
                },
                dateCreated: item.dateCreated || DateTime.now(),
            }));
            setData((prevData) => ({
                ...prevData,
                items: initialItems,
            }));
            setItemsValidation(
                props.documentUpdate.items.map((x) => ({
                    err: false,
                    message: "",
                    idGlobal: x.idGlobal,
                }))
            );
            setMnemocode(props.document.mnemocode)
            addDisplayItems(props.documentItems)

            const idStoreGlobal = props.documentUpdate.idSupplierStoreExternalGlobal;
            if (idStoreGlobal && props.documentItems.length > 0) {
                const newFilters: IItemFilter[] = [];
                let pending = props.documentItems.length;
                props.documentItems.forEach((item) => {
                    const goodsName = item.substance?.displayName || item.tradeName?.displayName;
                    const remainsFilter: IDistinctByStoreFilterData = {
                        goodsName,
                        idStoreGlobal,
                        cureForm: item.cureForm?.idGlobal,
                        dosage: item.dosage,
                        nFv: item.nFv,
                    };
                    requestForMovementDP.remains(remainsFilter, (remainsQty) => {
                        newFilters.push({ idItem: item.idGlobal, quantityMin: 1, quantityMax: remainsQty });
                        pending--;
                        if (pending === 0) {
                            setItemsFilter(newFilters);
                        }
                    });
                });
            }
        }
    }, [props.variant])

    useEffect(() => {
        const totalQuantity = data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        setQuantityGoods(totalQuantity);

        const totalRows = data.items.length;
        setQuantityRows(totalRows)
    }, [data.items])

    const addDisplayItems = (items: IRequestForMovementItemGetDTO[]) => {
        setDisplayItems([
            ...items.map((x: IRequestForMovementItemGetDTO): IDisplayItem => {
                return {
                    idGlobal: x.idGlobal,
                    dateCreated: x.dateCreated,
                    quantity: x.quantity,
                    nFv: x.nFv,
                    dosage: x.dosage,
                    cureFormName: x.cureForm?.displayName,
                    substanceName: x.substance?.displayName,
                    substanceNameLat: x.substanceNameLat,
                    tradeName: x.tradeName?.displayName,
                    tradeNameLat: x.tradeNameLat,
                    lots: x.lots
                }
            }),
        ])
    }

    const isValidDocument = (items: IRequestForMovementItemCreateDTO[], showMessage: () => void): boolean => {
        var flag = true
        if (!items || items.length === 0) {
            flag = false;
            showMessage()
        }
        return flag
    }

    const handleContractorChange = (value: IEntitySimpleDTO) => {
        setData((prevData) => ({
            ...prevData,
            idContractorExternalGlobal: value.idGlobal as string,
        }));

        if (data.idSupplierExternalGlobal === value.idGlobal) {
            setErrors({
                ...errors,
                sameContractorAndSupplier: errorsT("sameContractorAndSupplier"),
                idSupplierExternalGlobal: errorsT("sameContractorAndSupplier"),
            })
        } else {
            setErrors({
                ...errors,
                sameContractorAndSupplier: undefined,
                idSupplierExternalGlobal: undefined,
            })
        }
    }

    const handleSupplierChange = (value: IEntitySimpleDTO) => {
        setData((prevData) => ({
            ...prevData,
            idSupplierExternalGlobal: value.idGlobal as string,
        }));

        if (data.idContractorExternalGlobal === value.idGlobal) {
            setErrors({
                ...errors,
                sameContractorAndSupplier: errorsT("sameContractorAndSupplier"),
                idSupplierExternalGlobal: errorsT("sameContractorAndSupplier"),
            })
        } else {
            setErrors({
                ...errors,
                sameContractorAndSupplier: undefined,
                idSupplierExternalGlobal: undefined,
            })
        }
    }
    

    function loadContractorFrom(idGlobal: string) {
        contractorDP.getById(idGlobal, (e) => {
            setContractorFrom(e);
        })
    }

    function loadContractorTo(idGlobal: string) {
        contractorDP.getById(idGlobal, (e) => {
            setContractorTo(e);
        })
    }

    const addItem = (value: IChangeDataGrid) => {
        let quantity = value.value as number;
        let indexItem = data.items.findIndex((item) => item.idGlobal === value.idGlobal);
        let takeDataObj = data.items.find((item) => item.idGlobal === value.idGlobal) as IRequestForMovementItemCreateDTO;

        let indexDisplayItem = displayItems.findIndex((item) => item.idGlobal === value.idGlobal);
        let takeDisplayObj = displayItems.find((item) => item.idGlobal === value.idGlobal) as IDisplayItem;

        let newItem: IRequestForMovementItemCreateDTO = {
            ...takeDataObj,
            quantity: quantity,
        };

        let newDisplayItem: IDisplayItem = {
            ...takeDisplayObj,
            quantity: quantity,
        };

        setData({
            ...data,
            items: addValueToArray(data.items, indexItem, newItem),
        });
        setDisplayItems([...addValueToArray(displayItems, indexDisplayItem, newDisplayItem)]);
    }

    const calcRow = (value: IChangeDataGrid) => {
        addItem(value)
    }

    const onOkAction = () => {
        setSupplierSelection(false);
        lockingDocuments.delete({
            idTable: IdTableVariant.RequestForMovement,
            idDocument: data.idGlobal as string,
        });
        props.ok(data)
    }

    const hasQuantityExceeded = (): boolean => {
        if (!isQuantityControl) return false;
        return data.items.some((item) => {
            const filter = itemsFilter.find((f) => f.idItem === item.idGlobal);
            return filter !== undefined && item.quantity > filter.quantityMax;
        });
    }

    const onOk = () => {
        if (isValid()) {
            if (hasQuantityExceeded()) {
                setShowMessageModalValidDocument({ message: "Количество больше чем доступно для перемещения", show: true });
                return;
            }
            if (isValidDocument(data.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true) {
                onOkAction()
            }
        }
    }

    const onSave = () => {
        if (isValid()) {
            setErrors({
                ...errors,
                documentDate: undefined,
                idContractorExternalGlobal: undefined,
                idStoreExternalGlobal: undefined,
                idSupplierExternalGlobal: undefined,
                idSupplierStoreExternalGlobal: undefined,
            });

            if (hasQuantityExceeded()) {
                setShowMessageModalValidDocument({ message: "Количество больше чем доступно для перемещения", show: true });
                return;
            }

            if (isValidDocument(data.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true) {
                props.save(data)
            }
        }
    }

    const onCancel = () => {
        setSupplierSelection(false);
        operativeReserve.deleteAll(
            {
                idTable: IdTableVariant.RequestForMovement,
                idDocument: data.idGlobal as string,
            },
            () => {
                props.cancel()
            }
        )
        lockingDocuments.delete({
            idTable: IdTableVariant.RequestForMovement,
            idDocument: data.idGlobal as string,
        })
    }
    return (
        <>
            <BaseDocumentCreator
                valid={true}
                ok={{
                    onClick: onOk,
                    disabled: onlyRead ? true : false || !!itemsValidation.find((x) => x.err === true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                save={{
                    onClick: onSave,
                    disabled: onlyRead ? true : false || !!itemsValidation.find((x) => x.err === true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                print={{
                    onClick: () => {
                        return IdTableVariant.RequestForMovement
                    },
                    onSubItemClick: () => {
                        let arr = new Array<IKeyValuePair>()
                        if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal !== undefined) arr.push({ key: props.idGlobal, value: props.document.mnemocode })
                        return arr
                    },
                }}
                cancel={{ onClick: onCancel }}
            >
                <div className={createRequestForMovementStyles.glwrapper}>
                    <div className={createRequestForMovementStyles.wrapper}>
                        <div>
                            {
                                <>
                                    <>
                                        <FlexRow className={createRequestForMovementStyles.compactFieldFields}>
                                            <TextInput
                                                className={createRequestForMovementStyles.rowMnemcode}
                                                disabled
                                                label={t('documentBase.docNumberShort')}
                                                inline value={mnemocode}
                                            />
                                            <DateInput
                                                className={createRequestForMovementStyles.dateInput}
                                                required
                                                inline
                                                disabled={onlyRead}
                                                labelVariant="required"
                                                value={data.documentDate}
                                                onBlur={(value) => setData({ ...data, documentDate: value as DateTime })}
                                                label={t('documentBase.docDateShort')}
                                                error={errors.documentDate}
                                            />
                                        </FlexRow>
                                        <GridWrapper cols={2}>
                                            <StoreSelectorPanel
                                                we
                                                required
                                                documentType={DocumentType.requestForMovement}
                                                contractor={{
                                                    label: baseT("contractorTo"),
                                                    value: contractor,
                                                    onSelect: (value) => {
                                                        if (value) {
                                                            handleContractorChange(value)
                                                            setData((prevData) => ({
                                                                ...prevData,
                                                                idContractorExternalGlobal: value.idGlobal as string,
                                                            }))
                                                            setBaseFields((prevData) => ({
                                                                ...prevData,
                                                                idContractorExternalGlobal: value.idGlobal as string
                                                            }))
                                                            loadContractorTo(value.idGlobal);
                                                        }
                                                    },
                                                    onClear: () => {
                                                        setData((prevData) => ({
                                                            ...prevData,
                                                            idContractorExternalGlobal: "" as string,
                                                        }))
                                                        setBaseFields((prevData) => ({
                                                            ...prevData,
                                                            idContractorExternalGlobal: "" as string
                                                        }))
                                                        setErrors({
                                                            ...errors,
                                                            sameContractorAndSupplier: undefined,
                                                        })
                                                    },
                                                    disabled: onlyRead,
                                                    treeViewCheckDirectoryType: "pharmacy",
                                                }}
                                                store={{
                                                    label: baseT("storeTo"),
                                                    value: store,
                                                    onSelect: (value) => {
                                                        if (value) {
                                                            setData((prevData) => ({
                                                                ...prevData,
                                                                idStoreExternalGlobal: value.idGlobal as string,
                                                            }))
                                                            setBaseFields((prevData) => ({
                                                                ...prevData,
                                                                idStoreExternalGlobal: value.idGlobal as string
                                                            }))
                                                        }
                                                    },
                                                    onClear: () => {
                                                        setData((prevData) => ({
                                                            ...prevData,
                                                            idStoreExternalGlobal: "" as string,
                                                        }))
                                                        setBaseFields((prevData) => ({
                                                            ...prevData,
                                                            idStoreExternalGlobal: "" as string
                                                        }))
                                                    },
                                                    error: errors.idStoreExternalGlobal,
                                                    onFocus: () => setErrors({ ...errors, idStoreExternalGlobal: undefined }),
                                                    disabled: onlyRead,
                                                }}
                                                documentStore={{ idGlobal: data.idStoreExternalGlobal } as IEntitySimpleDTO}
                                                notAutoComplete={props.variant === "edit" || props.variant === "copy"}
                                                offUserFilter={true}
                                                excludeUserFilter={false}
                                            />
                                        </GridWrapper>
                                        <GridWrapper cols={2}>
                                            <StoreSelectorPanel
                                                required
                                                documentType={DocumentType.requestForMovement}
                                                change={onCanChangeData(data.items?.length)}
                                                isAcceptRequestForMovement={true}
                                                contractor={{
                                                    label: baseT("contractorFrom"),
                                                    value: contractor,
                                                    onSelect: (value) => {
                                                        if (value) {
                                                            handleSupplierChange(value)
                                                            setData((prevData) => ({
                                                                ...prevData,
                                                                idSupplierExternalGlobal: value.idGlobal as string,
                                                            }))
                                                            setBaseFields((prevData) => ({
                                                                ...prevData,
                                                                idSupplierExternalGlobal: value.idGlobal as string
                                                            }))
                                                            loadContractorFrom(value.idGlobal);
                                                        }
                                                    },
                                                    onClear: () => {
                                                        setData((prevData) => ({
                                                            ...prevData,
                                                            idSupplierExternalGlobal: "" as string,
                                                        }))
                                                        setBaseFields((prevData) => ({
                                                            ...prevData,
                                                            idSupplierExternalGlobal: "" as string
                                                        }))
                                                        setErrors({
                                                            ...errors,
                                                            sameContractorAndSupplier: undefined,
                                                            idSupplierExternalGlobal: undefined,
                                                        })
                                                    },
                                                    disabled: onlyRead,
                                                    treeViewCheckDirectoryType: "pharmacy",
                                                    error: errors.idSupplierExternalGlobal || errors.sameContractorAndSupplier,
                                                    onFocus: () =>
                                                        setErrors({
                                                            ...errors,
                                                            idSupplierExternalGlobal: undefined,
                                                            sameContractorAndSupplier: undefined,
                                                        }),
                                                }}
                                                store={{
                                                    label: baseT("storeFrom"),
                                                    value: store,
                                                    isAcceptRequestForMovement: true,
                                                    onSelect: (value) => {
                                                        setData((prevData) => ({
                                                            ...prevData,
                                                            idSupplierStoreExternalGlobal: value.idGlobal as string,
                                                        }))
                                                        setBaseFields((prevData) => ({
                                                            ...prevData,
                                                            idSupplierStoreExternalGlobal: value.idGlobal as string
                                                        }))
                                                    },
                                                    onClear: () => {
                                                        setData((prevData) => ({
                                                            ...prevData,
                                                            idSupplierStoreExternalGlobal: "" as string,
                                                        }))
                                                        setBaseFields((prevData) => ({
                                                            ...prevData,
                                                            idSupplierStoreExternalGlobal: "" as string
                                                        }))
                                                    },
                                                    error: errors.idSupplierStoreExternalGlobal,
                                                    onFocus: () => setErrors({ ...errors, idSupplierStoreExternalGlobal: undefined }),
                                                    disabled: onlyRead,
                                                }}
                                                notAutoComplete={props.variant === "edit" || props.variant === "copy"}
                                                documentStore={{ idGlobal: data.idSupplierStoreExternalGlobal } as IEntitySimpleDTO}
                                                offUserFilter={true}
                                                excludeUserFilter={true}
                                            />
                                        </GridWrapper>
                                        <Checkbox
                                            onChange={(checked) => setIsGetFromStore(checked)}
                                            name="IsGetFromStore"
                                            label="Подбирать из ТО"
                                            checked={isGetFromStore}
                                            disabled={onlyRead}
                                        />
                                        { isQuantityControlVisible &&
                                            <Checkbox
                                                onChange={(checked) => {
                                                    setIsQuantityControl(checked)
                                                    localStorage.setItem("quantityControl", JSON.stringify(checked))
                                                }}
                                                name="IsQuantityControl"
                                                label={baseT("quantityControl")}
                                                checked={isQuantityControl}
                                                disabled={isQuantityControlDisabled}
                                            />
                                        }
                                    </>
                                    <div className={createRequestForMovementStyles.field_flexblock}>
                                        <PositionEditPanel
                                            edit={{
                                                onClick: () => {
                                                    if (selectedItem) {
                                                        if (!selectedItem.idGlobal) return
                                                        const editItem = data.items.find((x) => x.idGlobal === selectedItem.idGlobal) as IRequestForMovementItemCreateDTO
                                                        if (editItem) {
                                                            setUpdateItem({
                                                                show: true,
                                                                item: editItem,
                                                                editItem: editItem,
                                                            })
                                                        }
                                                        if (!editItem) {
                                                            return
                                                        }
                                                    }
                                                },
                                                disabled: selectedItem && onlyRead,
                                            }}
                                            delete={{
                                                onClick: () => {
                                                    if (selectedItem) {
                                                        const itemToDelete = data.items.find((x) => x.idGlobal === selectedItem.idGlobal)
                                                        if (itemToDelete) {
                                                            const updatedItems = data.items.filter((x) => x.idGlobal !== selectedItem.idGlobal)
                                                            const updatedDisplayItems = displayItems.filter((x) => x.idGlobal !== selectedItem.idGlobal)
                                                            setData({ ...data, items: updatedItems })
                                                            setDisplayItems(updatedDisplayItems)
                                                            setSelectedItem(undefined)
                                                        }
                                                    }
                                                },
                                                disabled: selectedItem && onlyRead,
                                            }}
                                        />
                                        <div className={createRequestForMovementStyles.gridSelector}>
                                            <div className={createRequestForMovementStyles.gridSelectorInputWrapper}>
                                                <GridSelectorInput
                                                    disabled={onlyRead || !data.idSupplierStoreExternalGlobal}
                                                    selectorModalJsx={searchOption.value === SearchOption.substanceName ? SubstanceByStoreSelectorModal : TradeNameByStoreSelectorModal}
                                                    className={createRequestForMovementStyles.field_DocumentBase}
                                                    id={"inputAccompanyingSelectionGoods"}
                                                    inline={true}
                                                    label={t("documents.invoice.selectionOfGoods")}
                                                    searchOption={searchOption.value}
                                                    idTable={IdTableVariant.RequestForMovement}
                                                    onClear={() => { }}
                                                    onSelect={(value) => {
                                                        goodsDP.getById(value.idGlobal, (good) => {
                                                            setCreatorItem({
                                                                item: good as IGoodsDTO,
                                                                show: true,
                                                            })
                                                        })
                                                    }}
                                                    idStoreSupplier={isGetFromStore ? baseFields.idSupplierStoreExternalGlobal : undefined}
                                                    getUniqueGoodsByStore={isGetFromStore} // для получения уникальных значений по складу поставщика
                                                />
                                            </div>
                                            <Select
                                                disabled={onlyRead}
                                                onSelect={(option) => {
                                                    setSearchOption(option);
                                                }}
                                                defaultOption={defaultSearchOption}
                                                value={searchOption}
                                                className={createRequestForMovementStyles.field_SelectBlock}
                                                options={[
                                                    {
                                                        displayName: t("documents.requestForMovement.searchByMnn"),
                                                        value: SearchOption.substanceName,
                                                    },
                                                    {
                                                        displayName: t("documents.requestForMovement.searchByTn"),
                                                        value: SearchOption.tradeName,
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.gridWrapper}>
                                        <div className={styles.masterGrid}>
                                            <ChangeableGrid
                                                documentStateProc={onlyRead}
                                                autoSelect
                                                gridId={uuidv4()}
                                                data={displayItems}
                                                filter={gridFilter}
                                                totalCount={totalCount}
                                                plugin={RequestForMovementItemsGridPluginSettings}
                                                selectedItem={selectedItem}
                                                itemsFilter={itemsFilter}
                                                itemsValidation={itemsValidation}
                                                setItemsValidation={(value) => {
                                                    const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item))
                                                    setItemsValidation(newItemsValidation)
                                                }}
                                                getStyleCell={(cell, cells) => getStyleCell(cell, cells, DocumentType.requestForMovement)}
                                                onSelect={(row) => setSelectedItem(row)}
                                                onClick={(row) => setSelectedItem(row)}
                                                onSort={(i) => {
                                                    dispatchGridFilter({
                                                        type: "sort",
                                                        payload: i.propertyName,
                                                    })
                                                }}
                                                onFilterDelete={(i) =>
                                                    dispatchGridFilter({
                                                        type: "deleteColumnFilter",
                                                        payload: i.propertyName,
                                                    })
                                                }
                                                onPageNumberChange={(n) => {
                                                    dispatchGridFilter({
                                                        type: "changePageNumber",
                                                        payload: { pageNumber: n },
                                                    })
                                                }}
                                                onNumberPerPageChange={(n) => {
                                                    dispatchGridFilter({
                                                        type: "changeNumberPerPage",
                                                        payload: { numberPerPage: n },
                                                    })
                                                }}
                                                onColumnEnter={(value: IChangeDataGrid) => {
                                                    calcRow(value)
                                                }}
                                                onChangeData={(value: IChangeDataGrid) => {
                                                    calcRow(value)
                                                }}
                                                searching
                                                banEnterZerro
                                            />
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                        <Accordion opened={false} caption={``} id={`RequestForMovement-${uuidv4()}`} bold>
                            <GridWrapper cols={2}>
                                <NumberInput
                                    disabled
                                    inline
                                    label={t('documentBase.quantityStroke')}
                                    value={quantityRows}
                                />
                                <NumberInput
                                    disabled
                                    inline
                                    label={t('documentBase.quantityGoods')}
                                    value={quantityGoods}
                                />
                            </GridWrapper>
                            <GridWrapper cols={1} />
                        </Accordion>
                    </div>
                </div>
                {
                    creatorItem.show && creatorItem.item && !creatorItem?.editItem && (
                        <RequestForMovementCreatorItemModal
                            idDocumentGlobal={data.idGlobal as string}
                            idSupplierStoreExternalGlobal={baseFields.idSupplierStoreExternalGlobal}
                            idDocumentItemGlobal={creatorItem.editItem?.idGlobal ?? uuidv4()}
                            substance={searchOption.value === SearchOption.substanceName ? creatorItem.item?.substance as ISubstanceViewDTO : undefined}
                            tradeName={searchOption.value === SearchOption.tradeName ? creatorItem.item?.tradeName as ITradeNameViewDTO : undefined}
                            isGetFromStore={isGetFromStore}
                            isQuantityControl={isQuantityControl}
                            isQuantityControlVisible={isQuantityControlVisible}
                            items={data.items}
                            goods={creatorItem.item as IGoodsDTO}
                            ok={(createItem: ICreateItem) => {
                                let filteredItems: IRequestForMovementItemCreateDTO[] = data.items.filter((x) => x.idGlobal !== createItem.item.idGlobal)
                                let filteredDisplayItems: IDisplayItem[] = displayItems.filter((x) => x.idGlobal !== createItem.item.idGlobal)
                                setData({ ...data, items: [...filteredItems, createItem?.item] })
                                setDisplayItems([...filteredDisplayItems, { ...createItem?.displayItem }])
                                setItemsFilter([...itemsFilter, createItem?.filter])
                                setItemsValidation([...itemsValidation, { idGlobal: createItem?.item.idGlobal, message: "", err: false }])
                                setCreatorItem({ show: false, item: null })
                                setBaseFields({
                                    ...baseFields,
                                    dosage: createItem.item.dosage,
                                    nFv: createItem.item.nFv,
                                    quantity: createItem.item.quantity,
                                })
                            }}
                            cancel={() => setCreatorItem({ show: false, item: null })}
                        />
                    )
                }
                {
                    updateItem.show && updateItem.item && updateItem?.editItem && (
                        <RequestForMovementUpdateItemModal
                            idDocumentGlobal={data.idGlobal as string}
                            idDocumentItemGlobal={updateItem.editItem?.idGlobal}
                            idSupplierStoreExternalGlobal={baseFields.idSupplierStoreExternalGlobal}
                            isGetFromStore={isGetFromStore}
                            isQuantityControl={isQuantityControl}
                            isQuantityControlVisible={isQuantityControlVisible}
                            goods={updateItem.item as unknown as IGoodsDTO}
                            requestForMovementItemEdit={updateItem.editItem}
                            ok={(createItem: ICreateItem) => {
                                const updatedItems = data.items.filter((x) => x.idGlobal !== createItem.item.idGlobal)
                                setData({ ...data, items: [...updatedItems, createItem.item] })
                                const updatedDisplayItems = displayItems.filter((x) => x.idGlobal !== createItem.displayItem.idGlobal)
                                setDisplayItems([...updatedDisplayItems, createItem.displayItem])
                                setUpdateItem({ show: false, item: null, editItem: null })
                            }}
                            cancel={() => setUpdateItem({ show: false, item: null, editItem: null })}
                        />
                    )
                }
                {showMessageModal.show && (
                    <MessageModalWindow
                        message={showMessageModal.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" })
                                showMessageModal.BtSaveOrOk === "ok" ? onOkAction() : props.save(data)
                            },
                        }}
                        cancel={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" })
                            },
                        }}
                        primary
                    />
                )}
                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" })
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}
            </BaseDocumentCreator>
        </>
    )
}
